<script setup>

    import { computed, onMounted, ref, watch } from 'vue'

    import LatteIconMapPin from '@/Icons/LatteIconMapPin.vue'
    import LatteMapViewList from '@/Components/Maps/LatteMapViewList.vue'
    import LatteMapNearby from '@/Components/Maps/LatteMapNearby.vue'
    import LatteClientSummaryCard from '@/Components/LatteClientSummaryCard.vue'
    import Loader from '@/Components/App/Loader.vue'

    import { CARE_RECIPIENT_CONTACT_TYPE, util } from '@/Helpers'

    import { storeToRefs } from 'pinia'
    import { useMapStore, useGoogleMapsStore, useClientStore, useClientSingleStore, useViewStore } from '@/Stores'

    const mapStore = useMapStore()
    const googleMapsStore = useGoogleMapsStore()
    const clientStore = useClientStore()
    const clientSingleStore = useClientSingleStore()
    const viewStore = useViewStore()

    const { nearbyClients, nearbyError, areaSummary, selectedId, mapCenter, mapCenterCache, defaultCenter } = storeToRefs(mapStore)
    const { mapRef } = storeToRefs(googleMapsStore)
    const { clients } = storeToRefs(clientStore)
    const { client, clientAddress } = storeToRefs(clientSingleStore)
    const { isEmbedded } = storeToRefs(viewStore)

    const props = defineProps({
        mapCenter: {},
        useDefaultCenter: {
            type: Boolean,
            default: true
        },
        clientId: {
            type: [Number, String],
            default: null
        }
    })
    const loading = ref(false)

    const currentClientAddress = ref({
        address: null,
        city: null,
        state: null,
        zip: null
    })

    const hasValidAddress = computed(() => {
        const address = mapCenter.value?.address?.address?.trim() ?? ''
        const city = mapCenter.value?.address?.city?.trim() ?? ''

        return address.length > 0 || city.length > 0
    })

    async function loadWhosNearby(addressChanged = false) {
        loading.value = true

        if (mapCenterCache.value && !addressChanged) {
            mapCenter.value = mapCenterCache.value
        } else if (util.getQueryParam('address') && isEmbedded.value) {
            mapCenter.value = { address: util.objectifyAddressString(util.getQueryParam('address')), geo: { lat: 0, lng: 0 } }
        } else if (props.clientId && client.value && String(client.value.id) === String(props.clientId)) {
            mapCenter.value = { address: clientAddress.value, geo: client.value.geo }
        } else if (props.mapCenter) {
            mapCenter.value = { address: props.mapCenter.address, geo: props.mapCenter.geo }
        } else {
            // Use default center if enabled, otherwise set empty address for no address layout
            if (props.useDefaultCenter) {
                mapCenter.value = defaultCenter.value
            } else {
                mapCenter.value = { address: {}, geo: {} }
            }
        }

        if (!hasValidAddress.value) {
            loading.value = false
            return
        }

        const coffeeProfileId = props.clientId && client.value && String(client.value.id) === String(props.clientId)
            ? client.value.coffee_user_profile_id
            : null

        await clientStore.retrieveNearbyClients(util.stringifyAddressObject(mapCenter.value.address), coffeeProfileId).then(response => {
            nearbyError.value = null
            clientStore.hydrateFromNearby(response.data.clients)

            areaSummary.value = response.data.areaSummary

            nearbyClients.value = response.data.clients
            mapCenter.value.geo.lat = parseFloat(response.data.center.geo.lat)
            mapCenter.value.geo.lng = parseFloat(response.data.center.geo.lng)

            mapStore.resetMap()

        }).catch((err) => {
            nearbyError.value = err.error
        }).finally(() => {
            loading.value = false
        })
    }

    watch([() => mapRef.value?.ready], ([ready]) => {
        if (!ready) {
            return
        }

        if (props.clientId && client.value && (client.value.id != props.clientId)) {
            return
        }

        mapStore.resetMap()
    })

    watch([() => props.mapCenter], ([newMapCenter]) => {
        if (!mapRef.value?.ready) {
            return
        }

        mapStore.resetMap()
    }, { deep: true })

    watch([() => client.value], ([newClient]) => {
        if (!props.clientId) {
            return
        }

        if (newClient) {
            const contactTypeClient = newClient.contacts.filter(c => c.type === CARE_RECIPIENT_CONTACT_TYPE)[0] || null
            if (!contactTypeClient) {
                mapCenter.value = {
                    address: {},
                    geo: {}
                }
                currentClientAddress.value = {
                    address: null,
                    city: null,
                    state: null,
                    zip: null
                }
                return
            }

            const newAddress = util.stringifyAddressObject({
                address: contactTypeClient.address || '',
                city: contactTypeClient.city || '',
                state: contactTypeClient.state || '',
                zip: contactTypeClient.zip || ''
            })
            const currentAddress = util.stringifyAddressObject(currentClientAddress.value)
            if (newAddress === currentAddress) {
                return
            } else {
                currentClientAddress.value.address = contactTypeClient.address
                currentClientAddress.value.city = contactTypeClient.city
                currentClientAddress.value.state = contactTypeClient.state
                currentClientAddress.value.zip = contactTypeClient.zip
            }
        }

        loadWhosNearby(true)

        if (mapRef.value?.ready) {
            mapStore.resetMap()
        }
    }, { deep: true })

    onMounted(() => {
        if (util.getQueryParam('address') && isEmbedded.value) {
            loadWhosNearby()
        }
    })

</script>

<template>

    <div class="relative">

        <div
            v-if="!loading && !hasValidAddress"
            class="absolute inset-0 bg-white bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center rounded"
        >
            <div class="p-8 bg-white shadow-lg rounded-lg text-center">
                <h3 class="font-bold text-lg text-lifeworx-blue-800 mb-2">
                    No Address Provided
                </h3>
                <p class="text-sm text-gray-600">
                    Update the address in the contacts module to view nearby clients.
                </p>
            </div>
        </div>

        <div
            class="px-5 py-2.5 text-sm md:text-xs border-b text-lifeworx-blue-800 bg-white sticky top-28 md:top-0 md:relative md:flex"
            :class="{ 'py-0': isEmbedded }"
        >

            <span
                v-if="loading || nearbyError"
                class="text-gray-500"
            >
            &mdash;
            </span>

            <template v-else>

                <div class="flex-wrap flex-auto">

                    <span class="inline">
                        Clients &amp; prospects near
                    </span>
                    <span
                        class="tracking-wider hover:underline text-left cursor-pointer"
                        @click="mapStore.recenterMap"
                    >

                        <LatteIconMapPin class="text-lifeworx-green inline-block ml-0.5 -mt-1.5 -mb-1 h-4 w-4" />
                        <span class="font-bold text-lifeworx-green">
                            {{ mapCenter.address?.address + ', ' + mapCenter.address?.city + ', ' + mapCenter.address?.state + ' ' + mapCenter.address?.zip }}
                        </span>

                    </span>

                </div>

                <button
                    class="justify-end text-lifeworx-blue-500 hover:text-lifeworx-blue-700 font-semibold underline text-sm -mt-1"
                    @click="mapStore.resetMap()"
                >
                    Reset view
                </button>

            </template>

        </div>

        <div class="md:grid md:grid-cols-3 md:max-h-96">

            <div
                v-if="!loading && !nearbyError"
                class="shadow-inner border-r-2 h-48 md:h-96 overflow-scroll"
            >

                <LatteMapViewList
                    :data="nearbyClients"
                    :selected="selectedId"
                    @update:selected="mapStore.selectMarker"
                    @reset="mapStore.resetMap()"
                />

            </div>

            <div
                class="md:col-span-2 shadow-inner h-96 sticky bottom-14"
                :class="{
                    'md:col-span-3': loading || nearbyError,
                }"
            >

                <div class="h-96">

                    <LatteMapNearby
                        :mapCenter="mapCenter"
                        :nearbyClients="nearbyClients"
                    />

                </div>

                <div
                    v-if="selectedId && !loading && !nearbyError"
                    class="relative h-fit w-1/2 p-2 z-auto -top-[24rem] flex items-center"
                >

                    <div
                        class="w-full h-fit bg-white shadow p-4 overflow-scroll text-sm"
                    >

                        <LatteClientSummaryCard
                            :client="clients[selectedId]"
                            @close="mapStore.resetMap(false)"
                        />

                    </div>

                </div>

                <div
                    v-if="loading"
                    class="h-96 w-full opacity-50 bg-lifeworx-blue-800 relative -top-96 flex items-center justify-center"
                >
                    <Loader />
                </div>

                <div
                    v-else-if="nearbyError"
                    class="h-96 w-auto bg-opacity-50 bg-lifeworx-red-200 relative -top-96 flex items-center justify-center backdrop-blur-sm"
                >
                    <div class="p-10 text-lifeworx-red-950 text-center">

                        <h2 class="font-bold text-2xl">Error</h2>
                        <p class="w-96 my-5 font-bold">
                            {{ nearbyError.message }}
                        </p>
                        <p class="w-96 my-5 font-bold">
                            {{ nearbyError.response?.data.message }}
                        </p>

                        <button
                            v-if="![422].includes(nearbyError.response?.status)"
                            class="w-96 my-5 font-bold underline"
                            @click="loadWhosNearby()"
                        >
                            retry
                        </button>

                    </div>
                </div>

            </div>

        </div>

        <div class="px-5 py-2.5 text-sm md:text-xs border-t text-lifeworx-blue-800 bg-white sticky bottom-0 md:relative">

            <span
                v-if="loading || nearbyError"
                class="text-gray-500"
            >
                &mdash;
            </span>

            <template v-else>

                <span>
                    Showing {{ nearbyClients?.length }} closest of
                </span>
                <span class="font-bold text-lifeworx-purple">
                    {{ areaSummary?.clientCount }} {{ areaSummary?.clientCount == 1 ? 'client' : 'clients' }}
                </span>
                <span>
                    and
                </span>
                <span class="font-bold text-lifeworx-orange">
                    {{ areaSummary?.prospectCount }} {{ areaSummary?.prospectCount == 1 ? 'prospect' : 'prospects' }}
                </span>
                <span>
                    within 10 miles
                </span>

            </template>

        </div>

    </div>

</template>
